import { useLayoutEffect, useRef, useState } from 'react';

/**
 * Hook to calculate the height position for a magnifier and not overflow the container
 * @param mouseCoordinate - mouse coordinate
 * @param minChartCoordinate - min coordinate chart limit
 * @param maxChartCoordinate - max coordinate chart limit
 * @param layout - magnifier coordinate layout
 * @param isRotated - is magnifier rotated
 * @returns magnifier height position
 */
export const useMagnifierPosition = (
  mouseCoordinate: number,
  minChartCoordinate: number,
  maxChartCoordinate: number,
  layout: 'vertical' | 'horizontal',
  isRotated?: boolean,
) => {
  const [magnifierPosition, setMagnifierPosition] = useState<number>(0);
  const magnifierRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (magnifierRef?.current) {
      const magnifierSize =
        magnifierRef.current[
          layout === 'horizontal'
            ? isRotated
              ? 'clientHeight'
              : 'clientWidth'
            : 'clientHeight'
        ];

      const newMagnifierPosition = calculateYPosition(
        magnifierSize,
        mouseCoordinate,
        minChartCoordinate,
        maxChartCoordinate,
      );

      setMagnifierPosition(
        isRotated ? newMagnifierPosition + magnifierSize : newMagnifierPosition,
      );
    }
  }, [
    layout,
    mouseCoordinate,
    minChartCoordinate,
    maxChartCoordinate,
    isRotated,
  ]);

  return { magnifierPosition, magnifierRef };
};

export const calculateYPosition = (
  magnifierSize: number,
  mouseCoordinate: number,
  minChartCoordinate: number,
  maxChartCoordinate: number,
) => {
  const halfMagnifierSize = magnifierSize / 2;
  const mousePositionFromViewPort = minChartCoordinate + mouseCoordinate;

  let newMagnifierPosition = mousePositionFromViewPort - halfMagnifierSize;

  if (mousePositionFromViewPort <= minChartCoordinate + halfMagnifierSize) {
    newMagnifierPosition = minChartCoordinate;
  }

  if (mousePositionFromViewPort >= maxChartCoordinate - halfMagnifierSize) {
    newMagnifierPosition = maxChartCoordinate - magnifierSize;
  }

  return newMagnifierPosition;
};
